/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BAProfile, BAProvidersSync } from '../../api/user/user.models';
import { useAnalyticsSession } from '../../hooks/analytics/useAnalyticsSession';
import { useGetUserAccount } from '../../hooks/user/useGetUserAccount';
import { useValidateUserSession } from '../../hooks/user/useValidateUserSession';
import { useGlobalStore } from '../../stores/global/global.store';
import { useUserProfileStore } from '../../stores/user-profile/user-profile.store';
import { Button } from '../../ui-library/Elements/Button';
import { getAuthToken } from '../../utils/auth';
import { getAccountProviders } from '../../utils/providers';
import { Login } from '../Login';

export interface Props {
  requireAuth?: boolean;
  children?: ReactNode;
}

const SessionProvider = ({ requireAuth, children }: Props) => {
  const queryClient = useQueryClient();
  const { t: tCommon } = useTranslation('common', { keyPrefix: 'common' });
  const [loading, setLoading] = useState(true);

  const getUserAccount = useGetUserAccount();
  const { setBanner } = useGlobalStore();
  const { resetProfile, updateProfile } = useUserProfileStore();
  const authToken = getAuthToken();
  const needsAuthentication = requireAuth && !authToken?.token;

  useValidateUserSession();
  useAnalyticsSession();

  const setUserProfile = (profile: BAProfile, providers: BAProvidersSync[]) => {
    const { accounts, gameLibrary, nickName } = profile;
    const gameProviders = providers;
    const { accountProviders, accountStatus, defaultProvider } = getAccountProviders(accounts, providers, gameLibrary);

    updateProfile({
      accountProviders,
      accountStatus,
      defaultProvider,
      nickName: nickName,
    });

    const synced = gameProviders.find((provider) => provider.status === 'synced');
    if (gameProviders.length > 1 && !synced) {
      // TODO: Need to refactor how and when this banner is displayed. Disabling for now.
      setBanner({
        display: false,
        description: tCommon('alert.sync-platforms'),
        theme: 'info',
        children: (
          <Button
            variant='secondary'
            size='xsmall'
            onClick={() => {
              void open(`${process.env.NEXT_PUBLIC_FE_BA_URL}/account/my-account/games/dbd`);
            }}
          >
            {tCommon('try-now')}
          </Button>
        ),
      });
    }
  };

  useEffect(() => {
    if (getUserAccount.userProfile.isSuccess && getUserAccount.userProviders.isSuccess) {
      setUserProfile(getUserAccount.userProfile.data, getUserAccount.userProviders.data);
    }
    setLoading(false);
  }, [
    getUserAccount.userProfile.data,
    getUserAccount.userProfile.status,
    getUserAccount.userProviders.data,
    getUserAccount.userProviders.status,
  ]);

  useEffect(() => {
    if (!authToken) {
      setBanner(undefined);
      resetProfile();
      queryClient.clear();
      document.body.removeAttribute('data-auth');
    } else {
      document.body.setAttribute('data-auth', 'true');
    }
  }, [authToken]);

  if (loading) return null;

  return needsAuthentication ? <Login /> : children;
};

export default SessionProvider;
